import * as Actions from '../actions/campaignActions';
import Campaign from '../../entities/campaign';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CampaignAction = { 
    type: string; 
    campaign: Campaign | null; 
    campaignId: string | Campaign; 
    partial: boolean; 
    lastFetch: number; 
    campaigns: Campaign[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Campaign> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Campaign[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Campaign> | null, firmId: string, userId: string, updates?: Campaign[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'campaign', 'campaigns', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Campaign>} data
 * @param {Campaign[]} [updates]
 * @returns {ArrayMap<Campaign>}
 */
function updateStorage(data: ArrayMap<Campaign>, updates?: Campaign[]): ArrayMap<Campaign> {
	if (logging) console.debug('⮂ campaignReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Campaign[]} [updates]
 * @returns {ArrayMap<Campaign>}
 */
function updateStorageNoReturn(updates?: Campaign[]): void {
	if (logging) console.debug('⮂ campaignReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Campaign[]} [data]
 * @returns {ArrayMap<Campaign>}
 */
function createState(data?: Campaign[] | ArrayMap<Campaign>): ArrayMap<Campaign> {
	if (!data) return ArrayMapRegistry.clone<Campaign>('campaign', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Campaign>('campaign', data.map(e => e));
    else return ArrayMapRegistry.clone<Campaign>('campaign', data);
}

function campaigns(state: ArrayMap<Campaign>, action: CampaignAction): ArrayMap<Campaign> {
	if (!state) state = createState();

	let st: ArrayMap<Campaign>;

	switch (action.type) {
		// action.campaign - the new value of campaign with id action.id
		// action.campaignId - id of the target, used if new value is null
		case Actions.CAMPAIGN_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ campaignReducer: FETCH_ONE_SUCCESS', action);

			if (action.campaign === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.campaignId)) {
				st = state.clone();
				st.set(action.campaign);
				// state.clear();

				updateStorageNoReturn([action.campaign]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.campaigns
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.campaigns
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.campaigns
			 !partial, !stateEmpty, fetchEmpty	-> D return campaigns in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge campaigns in state newer than those in action.campaigns
		 */
		case Actions.CAMPAIGN_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.campaigns.length === 0;
			const fetched = action.campaigns.map(campaign => new Campaign({...campaign, persisted: true}));

			if (logging) console.debug(`⮂ campaignReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} campaigns with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ campaignReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ campaignReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Campaign> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Campaign[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(campaign => {
					const current = st.get(campaign.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && campaign.status === 'D') {
							// Remove it from state
							toUpdate.push(campaign);
							st.delete(campaign);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (campaign.addedOn > current.addedOn) {
								toUpdate.push(campaign);
								st.set(campaign);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (campaign.status !== 'D') {
							toUpdate.push(campaign);
							st.set(campaign);
						}
						else if (logging) console.debug('⮂ campaignReducer: partialMerge: skipping deleted campaign not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ campaignReducer: empty: campaigns', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ campaignReducer: fullMerge: campaigns', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localCampaign => {
                    // If this campaign has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localCampaign.persisted) {
                        const fetchedCampaign = st.get(localCampaign.id);
                        if (!fetchedCampaign || localCampaign.addedOn > fetchedCampaign.addedOn) st.set(localCampaign);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.CAMPAIGN_FETCH_ONE_FAILURE:
			console.error('CAMPAIGN_FETCH_ONE_FAILURE', action.campaignId, action.err);

			// No update to storage required
			return state;

		case Actions.CAMPAIGN_FETCH_FAILURE:
			console.error('CAMPAIGN_FETCH_FAILURE', action.campaignId, action.err);

			// No update to storage required
			return state;

		case Actions.CAMPAIGN_RESTORE: {
		    let campaign = new Campaign({...action.campaign});
            st = state.clone();

            const existing = st.get(campaign.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (campaign.addedOn >= existing.addedOn) {
                    st.set(campaign);
                    return updateStorage(st, [campaign]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (campaign.status === 'D') return state;

                st.set(campaign);
                return updateStorage(st, [campaign]);
            }
        }
            
		case Actions.CAMPAIGN_SAVE: {
		    // console.debug('⮂ CAMPAIGN_SAVE reducer', action.campaign?.id);
		    
			// clone the campaign
			let campaign = new Campaign({...action.campaign});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new campaign in the state
			// If this is a create, add to the queue
			const _campaign = st.get(campaign.id);

			if (!_campaign) st.set(campaign);
			else {
				if (campaign.addedOn >= _campaign.addedOn) st.set(campaign);
			}

			return updateStorage(st, [campaign]);
		}

		case Actions.CAMPAIGN_SAVE_MANY: {
		    if (logging) console.debug('⮂ CAMPAIGN_SAVE_MANY reducer', action.campaigns.map(campaign => campaign.id).join(','));
		    
			// clone the campaigns
			let toProcess = action.campaigns.map(campaign => new Campaign(campaign));

			st = state.clone();
			// state.clear();

			const updates: Campaign[] = [];

			toProcess.forEach(campaign => {
				// Add or update the new campaign in the state
				// If this is a create, add to the queue
				const existing = st.get(campaign.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (campaign.status !== 'D') {
						st.push(campaign);
						updates.push(campaign);
					}
					else if (logging) console.debug('⮂ CAMPAIGN_SAVE_MANY: skipping deleted campaign not in state');
				}
				else {
					if (campaign.addedOn >= existing.addedOn) {
						st.set(campaign);
						updates.push(campaign);
					}
					else if (logging) console.debug('⮂ Skipping, existing campaign had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CAMPAIGN_REMOVE: {
			// clone the campaign
			const campaign = new Campaign({...action.campaign});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(campaign);

			return updateStorage(st, [campaign]);
		}

		case Actions.CAMPAIGN_RESTORE_FAILURE:
		case Actions.CAMPAIGN_SAVE_FAILURE: {
		    console.warn('CAMPAIGN_SAVE_FAILURE reducer', action.campaign?.id);
		    if (!action.campaign?.id) {
				console.warn('CAMPAIGN_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const campaign = ensure(Campaign, action.campaign);
			if (!campaign) {
				console.error('Could not find campaign after call to save');
				return state;
			}

			st = state.clone();

			const _campaignRef = state.get(campaign.id);

			if (_campaignRef && (_campaignRef.addedOn === campaign.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _campaign = new Campaign({..._campaignRef});
				_campaign.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_campaign.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('campaign', _campaign).then(r => {
						if (r.isErr()) console.error(`⮂ campaignReducer: failed to save campaign with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ campaignReducer: saved campaign with fatalError info');
					}).catch(err => console.error(`⮂ campaignReducer: failed to save campaign with fatalError info: ${err.message}`));

				st.set(_campaign);
			}

			return updateStorage(st);
		}

		case Actions.CAMPAIGN_SAVE_MANY_FAILURE: {
		    console.warn('CAMPAIGN_SAVE_MANY_FAILURE reducer', action.campaigns.map(campaign => campaign.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.CAMPAIGN_RESTORE_SUCCESS:
		case Actions.CAMPAIGN_SAVE_SUCCESS: {
			if (!action.campaign?.id) {
				console.warn('CAMPAIGN_SAVE_SUCCESS reducer');
                return state;
			}

			const campaignRef = state.get(action.campaign?.id);
			if (!campaignRef) {
				console.warn('Could not find campaign after call to save');
                return state;
			}

			st = state.clone();

			if (action.campaign.addedOn >= campaignRef.addedOn) {
				st.set(action.campaign);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const campaign = new Campaign({...campaignRef, persisted: true});
				st.set(campaign);
			}

			return updateStorage(st, [action.campaign]);
		}

		case Actions.CAMPAIGN_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ CAMPAIGN_SAVE_MANY_SUCCESS reducer', action.campaigns.map(campaign => campaign.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const campaigns: Campaign[] = [];

			action.campaigns.forEach(e => {
				const campaignRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!campaignRef) console.warn('Could not find campaign after call to save');
				else if (e.addedOn >= campaignRef.addedOn) {
				    st.set(e);
				    campaigns.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const campaign = new Campaign({...campaignRef, persisted: true});
					st.set(campaign);
					campaigns.push(campaign);
				}
			});

			return updateStorage(st, campaigns);
		}

		case Actions.CAMPAIGN_REMOVE_FAILURE: {
		    console.warn('CAMPAIGN_REMOVE_FAILURE action', action.campaign?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.CAMPAIGN_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.CAMPAIGN_RESTORE_MANY: {
			if (logging) console.debug('⮂ CAMPAIGN_RESTORE_MANY reducer', action.campaigns.map(campaign => campaign.id).join(','));

			const toProcess = action.campaigns.map(campaign => new Campaign({...campaign}));
			st = state.clone();

			const updates: Campaign[] = [];

			toProcess.forEach(campaign => {
				const existing = st.get(campaign.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (campaign.addedOn >= existing.addedOn) {
						st.set(campaign);
						updates.push(campaign);
					}
					else if (logging) console.debug('⮂ CAMPAIGN_RESTORE_MANY: skipping older version of campaign');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (campaign.status !== 'D') {
						st.set(campaign);
						updates.push(campaign);
					}
					else if (logging) console.debug('⮂ CAMPAIGN_RESTORE_MANY: skipping deleted campaign not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CAMPAIGN_REMOVE_MANY: {
			if (logging) console.debug('⮂ CAMPAIGN_REMOVE_MANY reducer', action.campaigns.map(campaign => campaign.id).join(','));

			const toProcess = action.campaigns.map(campaign => new Campaign({...campaign}));
			st = state.clone();

			toProcess.forEach(campaign => {
				st.delete(campaign);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default campaigns;